import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { IAddAssetFieldError, IAssetType, IAssetTypeRecord, IBulkAddErrorResult } from '../interfaces/asset.interface';
import { resolveTicketErrorMessage } from '../utils/ticket-error.util';
import { ASSET_TYPES } from './asset.constants';

export const ADD_ASSET_NEW_CATEGORY_CODES = ['WC', 'SW', 'FW', 'AP', 'S'];

export function buildAddAssetCategories(assetTypes: IAssetTypeRecord[] | null | undefined): IAssetType[] {
  const records = Array.isArray(assetTypes) ? assetTypes : [];
  const newCategories = ADD_ASSET_NEW_CATEGORY_CODES.map((code) =>
    records.find((record) => record.assetTypeCode === code),
  )
    .filter((record): record is IAssetTypeRecord => !!record)
    .map((record) => ({
      assetId: record.id,
      assetName: record.name,
      assetCode: record.assetTypeCode,
    }));
  return [...ASSET_TYPES, ...newCategories];
}

export const ADD_ASSET_SERIAL_CATEGORIES = [
  'Monitor',
  'Laptop',
  'Mobile Phone',
  'Webcam',
  'Switch',
  'Firewall',
  'Access Point',
];

export const ADD_ASSET_DEVICE_SERIAL_CATEGORIES = ['Webcam', 'Switch', 'Firewall', 'Access Point'];

export const ADD_ASSET_NO_PROJECT_CATEGORIES = ['Webcam', 'Switch', 'Firewall', 'Access Point', 'Speaker'];

export const ADD_ASSET_DEFAULT_QUANTITY = 1;

export const ADD_ASSET_MAX_QUANTITY = 50;

export const ADD_ASSET_NON_DIGIT_PATTERN = /\D/g;

export function buildQuantityInvalidMessage(maxQuantity: number): string {
  return `Enter a whole number between 1 and ${maxQuantity}.`;
}

export function buildQuantityMaxMessage(maxQuantity: number): string {
  return `Quantity cannot exceed ${maxQuantity} per submission.`;
}

export const ADD_ASSET_MESSAGES = {
  quantityPlaceholder: String(ADD_ASSET_DEFAULT_QUANTITY),
  quantityInvalid: buildQuantityInvalidMessage(ADD_ASSET_MAX_QUANTITY),
  quantityMax: buildQuantityMaxMessage(ADD_ASSET_MAX_QUANTITY),
  connectionLost: 'Looks like you lost your connection. Please check your connection and try again',
  mandatory: 'This field is mandatory',
  serialPlaceholder: 'Enter Serial No',
  serialDuplicate: 'Duplicate Serial No within this batch.',
  reduceQuantityTitle: 'Reduce quantity?',
  reduceQuantityContinue: 'Continue',
  reduceQuantityCancel: 'Cancel',
  bulkConfirmTitle: 'Add assets to inventory?',
  bulkConfirm: 'Confirm',
  bulkCancel: 'Cancel',
  purchaseDateFuture: 'Purchase Date cannot be a future date.',
  somethingWentWrong: 'Something went wrong!',
  bulkRowsInvalid: 'No assets were added. Please correct the highlighted rows and try again.',
  bulkSharedFieldsInvalid: 'Some details are invalid. No assets were added. Please review the form and try again.',
  bulkSystemError: 'Unable to add assets due to a system error. No assets were added. Please try again.',
};

export const ADD_ASSET_UNIT_FIELD_PATTERN = /^units\[(\d+)\]\./;

export function toAddAssetApiDate(purchaseDate: string | null | undefined): string {
  return (purchaseDate ?? '').split('-').reverse().join('/');
}

function collectUnitRowErrors(entries: unknown): Record<number, string> {
  const rowErrors: Record<number, string> = {};
  if (!Array.isArray(entries)) return rowErrors;
  entries.forEach((entry) => {
    const item = entry as Record<string, unknown>;
    const path = String(item?.['field'] ?? item?.['param'] ?? item?.['path'] ?? '');
    const message = item?.['message'] ?? item?.['msg'];
    const match = ADD_ASSET_UNIT_FIELD_PATTERN.exec(path);
    if (match && typeof message === 'string' && !(Number(match[1]) in rowErrors)) {
      rowErrors[Number(match[1])] = message;
    }
  });
  return rowErrors;
}

export function resolveBulkAddError(error: HttpErrorResponse): IBulkAddErrorResult {
  const errorList = error?.error?.error?.errorList;
  const status = error?.status ?? 0;
  if (status === 0 || !error?.error) {
    return { banner: ADD_ASSET_MESSAGES.connectionLost, rowErrors: {}, assetCodes: null };
  }
  if (status >= 500) {
    return { banner: ADD_ASSET_MESSAGES.bulkSystemError, rowErrors: {}, assetCodes: null };
  }
  if (status !== 400) {
    const assetCodes = Array.isArray(errorList?.assetCodes) ? (errorList.assetCodes as string[]) : null;
    return {
      banner: resolveTicketErrorMessage(error, ADD_ASSET_MESSAGES.somethingWentWrong, ADD_ASSET_MESSAGES.connectionLost),
      rowErrors: {},
      assetCodes,
    };
  }
  const rowErrors = collectUnitRowErrors(Array.isArray(errorList) ? errorList : errorList?.fields);
  return {
    banner: Object.keys(rowErrors).length ? ADD_ASSET_MESSAGES.bulkRowsInvalid : ADD_ASSET_MESSAGES.bulkSharedFieldsInvalid,
    rowErrors,
    assetCodes: null,
  };
}

export function validateAddAssetPurchaseDate(control: AbstractControl): ValidationErrors | null {
  const [year, month, day] = String(control.value ?? '').split('-').map(Number);
  if (!year || !month || !day) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(year, month - 1, day) > today ? { futureDate: true } : null;
}

export function parseAddAssetQuantity(raw: unknown): number | null {
  const value = String(raw ?? '').trim();
  if (!/^\d+$/.test(value)) return null;
  const quantity = Number(value);
  return quantity >= 1 ? quantity : null;
}

export function getAddAssetQuantityError(raw: unknown, maxQuantity: number = ADD_ASSET_MAX_QUANTITY): string {
  const quantity = parseAddAssetQuantity(raw);
  if (quantity === null) return buildQuantityInvalidMessage(maxQuantity);
  if (quantity > maxQuantity) return buildQuantityMaxMessage(maxQuantity);
  return '';
}

export function resolveAddAssetMaxQuantity(max: unknown): number {
  return Number.isInteger(max) && (max as number) >= ADD_ASSET_DEFAULT_QUANTITY
    ? (max as number)
    : ADD_ASSET_MAX_QUANTITY;
}

export function buildAddAssetErrorMessage(error: HttpErrorResponse): string {
  const fields = error?.error?.error?.errorList?.fields;
  const fieldMessages = Array.isArray(fields)
    ? (fields as IAddAssetFieldError[]).map((field) => field?.message).filter((message): message is string => !!message)
    : [];
  if (fieldMessages.length) return [...new Set(fieldMessages)].join(' ');
  return resolveTicketErrorMessage(error, ADD_ASSET_MESSAGES.somethingWentWrong, ADD_ASSET_MESSAGES.connectionLost);
}

export function createAddAssetSubmissionKey(): string {
  return crypto.randomUUID();
}

export function findDuplicateSerialIndexes(serials: string[]): number[] {
  const indexesBySerial = new Map<string, number[]>();
  serials.forEach((serial, index) => {
    const key = (serial ?? '').trim();
    if (!key) return;
    indexesBySerial.set(key, [...(indexesBySerial.get(key) ?? []), index]);
  });
  return [...indexesBySerial.values()].filter((indexes) => indexes.length > 1).flat();
}

export function countFilledSerialsBeyond(serials: string[], keepCount: number): number {
  return serials.slice(keepCount).filter((serial) => (serial ?? '').trim()).length;
}

export function buildBulkAssetCodeMessage(quantity: number): string {
  return `${quantity} unique asset codes will be generated on save.`;
}

export function buildBulkAddConfirmMessage(quantity: number, category: string): string {
  return `You are about to add ${quantity} ${category} assets to inventory. Confirm?`;
}

export function buildUnitFieldLabel(label: string, index: number, quantity: number): string {
  return quantity > 1 ? `${label} ${index + 1}` : label;
}

export function stripNonDigits(value: unknown): string {
  return String(value ?? '').replace(ADD_ASSET_NON_DIGIT_PATTERN, '');
}

export function buildReduceQuantityMessage(removedCount: number): string {
  return `Reducing the quantity will remove ${removedCount} Serial No row(s) that contain values. Continue?`;
}
